/**
 * Which chapters the viewer has opened — kept in localStorage so the dots
 * and the "done" marks survive a reload. Never load-bearing: a throwing or
 * absent storage just means a fresh tour every time.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useCallback, useEffect, useState } from 'react';

export const TUTORIAL_STORAGE_KEY = 'trafficpulse-dg-tutorial-v1';
/** Fired on every write so other mounted readers (the header button) follow. */
const CHANGE_EVENT = 'trafficpulse-dg-tutorial-change';

function read(): number[] {
  try {
    const raw = localStorage.getItem(TUTORIAL_STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed) ? parsed.filter((n): n is number => Number.isInteger(n)) : [];
  } catch {
    return [];
  }
}

function write(visited: number[]): void {
  try {
    localStorage.setItem(TUTORIAL_STORAGE_KEY, JSON.stringify(visited));
    window.dispatchEvent(new Event(CHANGE_EVENT));
  } catch {
    /* storage unavailable — the tour still works */
  }
}

export function useTutorialProgress() {
  const [visited, setVisited] = useState<number[]>(read);
  useEffect(() => {
    const sync = () => setVisited(read());
    window.addEventListener(CHANGE_EVENT, sync);
    return () => window.removeEventListener(CHANGE_EVENT, sync);
  }, []);
  const markVisited = useCallback((chapter: number) => {
    setVisited((prev) => {
      if (prev.includes(chapter)) return prev;
      const next = [...prev, chapter].sort((a, b) => a - b);
      write(next);
      return next;
    });
  }, []);
  const reset = useCallback(() => {
    setVisited([]);
    write([]);
  }, []);
  return { visited, markVisited, reset, hasOpened: visited.length > 0 };
}
